import React, { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import { zhCN } from "date-fns/locale";
import { useTranslation } from "react-i18next";

interface ClockDisplayProps {
  currentTime: Date;
}

interface FlipCardProps {
  digit: string;
  previousDigit?: string;
}

/**
 * 翻页卡片组件
 */
const FlipCard: React.FC<FlipCardProps> = ({ digit, previousDigit }) => {
  const [isFlipping, setIsFlipping] = useState(false);

  useEffect(() => {
    if (previousDigit !== undefined && previousDigit !== digit) {
      setIsFlipping(true);
      const timer = setTimeout(() => setIsFlipping(false), 600);
      return () => clearTimeout(timer);
    }
  }, [digit, previousDigit]);

  return (
    <div className="flip-card-container">
      <div className={`flip-card ${isFlipping ? "flipping" : ""}`}>
        {/* 上半部分 - 显示当前数字的上半部分 */}
        <div className="flip-card-top">
          <span className="flip-card-digit">
            {isFlipping ? previousDigit : digit}
          </span>
        </div>

        {/* 下半部分 - 显示当前数字的下半部分 */}
        <div className="flip-card-bottom">
          <span className="flip-card-digit">{digit}</span>
        </div>

        {/* 翻页动画的前半部分 - 旧数字翻走 */}
        {isFlipping && (
          <div className="flip-card-front">
            <span className="flip-card-digit">{previousDigit}</span>
          </div>
        )}

        {/* 翻页动画的后半部分 - 新数字翻来 */}
        {isFlipping && (
          <div className="flip-card-back">
            <span className="flip-card-digit">{digit}</span>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * 主时钟显示组件 - 翻页时钟效果
 */
export const ClockDisplay: React.FC<ClockDisplayProps> = ({ currentTime }) => {
  const { t, i18n } = useTranslation(["v3/clock"]);
  const previousTimeRef = useRef<string>("");
  const [isInitialized, setIsInitialized] = useState(false);

  // 格式化时间
  const timeString = format(currentTime, "HH:mm");
  // const secondsString = format(currentTime, 'ss');

  // 格式化日期
  const dateString = format(currentTime, "yyyy年MM月dd日", {
    locale: i18n.language === "zh" ? zhCN : undefined,
  });

  const weekdayString = format(currentTime, "EEEE", {
    locale: i18n.language === "zh" ? zhCN : undefined,
  });

  // 拆分时间为独立数字
  const timeDigits = timeString.replace(":", "");
  const prevTimeDigits = previousTimeRef.current
    ? previousTimeRef.current.replace(":", "")
    : timeDigits;

  useEffect(() => {
    if (!isInitialized) {
      // 首次初始化，直接设置当前时间，不触发动画
      previousTimeRef.current = timeString;
      setIsInitialized(true);
    } else {
      // 后续更新，先触发动画，再更新 previousTime
      const timer = setTimeout(() => {
        previousTimeRef.current = timeString;
      }, 300); // 动画进行一半时更新

      return () => clearTimeout(timer);
    }
  }, [timeString, isInitialized]);

  return (
    <div className="flex flex-col items-center justify-center text-center h-full">
      {/* 翻页时钟显示 */}
      <div className="clock-display-container clock-digit-font">
        {/* 小时 */}
        <div className="clock-display-digit-group">
          <span className="clock-display-digit">{timeDigits[0]}</span>
          <span className="clock-display-digit">{timeDigits[1]}</span>
        </div>

        {/* 分隔符 */}
        <div className="clock-display-separator">:</div>

        {/* 分钟 */}
        <div className="clock-display-digit-group">
          <span className="clock-display-digit">{timeDigits[2]}</span>
          <span className="clock-display-digit">{timeDigits[3]}</span>
        </div>

        {/* 秒数 */}
        {/* <div className="flip-seconds">
          {secondsString}
        </div> */}
      </div>

      {/* 日期信息 */}
      <div className="mt-[12px]">
        <div className="clock-display-date">
          {dateString}
          &nbsp;
          {weekdayString}
        </div>
      </div>
    </div>
  );
};

export default ClockDisplay;
